import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import jwt_decode from 'jwt-decode'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Banner } from '@dsny/dsny-component-library'
import LoginCard from '../components/LoginCard'
import StepOne from './Steps/StepOne'
import StepTwo from './Steps/StepTwo'
import StepThree from './Steps/StepThree'
import AuthLayout from '../AuthLayout'
import AuthRolloutBoundary from '../LoginWithRollout'
import { SignUpStepsCounter, Wrapper } from './Steps/Steps.styles'
import { thunks as AuthThunks } from 'reducers/Auth'
import SignUpResult from './Steps/SignUpResult'
import { FooterText, SignUpLink } from '../components/AuthForm.styles'
import { AUTH_ERRORS, RELATIONSHIP_TYPES } from 'constants/enums'
import { RESPONSIVE_BREAKPOINT_PAGES } from 'constants/responsiveBreakpoints'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'

const decodeToken = (token) => {
  try {
    return token ? jwt_decode(token) : null
  } catch (error) {
    console.error('Invalid token:', error.message)
    return null
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const SignupResponsiveWrapper = ({ children }) => {
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const responsiveBreakpointsV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2

  return (
    <Wrapper
      $responsiveBreakpointsV2={responsiveBreakpointsV2}
      data-cy="signup-flow">
      {children}
    </Wrapper>
  )
}

SignupResponsiveWrapper.propTypes = {
  children: PropTypes.node.isRequired
}

const Signup = ({
  responsiveBreakpointsPage = RESPONSIVE_BREAKPOINT_PAGES.SIGNUP,
  responsiveBreakpointsRollout = false
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { token } = useParams()
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [error, setError] = useState('')
  const [isSubmissionSuccess, setSubmissionSuccess] = useState(false)
  const [signUpData, setSignUpData] = useState({})
  const [emailToken, setEmailToken] = useState('')
  const [isVerificationCodeInvalid, setVerificationCodeInvalid] =
    useState(false)
  const decodedToken = useMemo(() => decodeToken(token), [token])

  /*This is a temporary commented block of code, we'll be back once the BE improves response times*/

  // const onLoginWithPasswordSubmit = async (values) => {
  //   const maxRetries = 3 // Maximum number of retry attempts
  //   const baseDelay = 1000 // Initial delay in milliseconds (1 second)

  //   let attempt = 0

  //   while (attempt < maxRetries) {
  //     try {
  //       await dispatch(
  //         AuthThunks.loginWithPassword({
  //           email: values.email,
  //           password: values.password,
  //           publicDevice: values.publicDevice || null
  //         })
  //       )

  //       // If successful, navigate and break the retry loop
  //       navigate('/releases')
  //       return
  //     } catch (error) {
  //       const { data } = error
  //       console.error(`Attempt ${attempt + 1} failed:`, error?.data)

  //       if (data?.code === 401) {
  //         setError(AUTH_ERRORS.INVALID_CREDENTIALS)
  //       }

  //       attempt++

  //       if (attempt < maxRetries) {
  //         // Wait for an exponential backoff delay before retrying
  //         const delay = baseDelay * 2 ** (attempt - 1)
  //         console.warn(`Retrying in ${delay}ms...`)
  //         await sleep(delay)
  //       } else {
  //         console.error('Max retries reached. Operation failed.')
  //       }
  //     }
  //   }
  // }
  const goToLoginPage = (successSignUpCoworker) => {
    navigate(`/?successSignUpCoworker=${successSignUpCoworker}`)
  }

  const goNextStep = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex((prevIndex) => prevIndex + 1)
    }
  }

  const isStepCounterBlocked = (error) =>
    error === AUTH_ERRORS.CHECK_EMAIL_FAILED ||
    error === AUTH_ERRORS.INVALID_EMAIL_TOKEN

  const forceGoToPage = (page) => {
    window.location.href = page
  }

  const submitStepOne = async (data) => {
    if (!data?.hasAcceptedTerms) {
      setError(AUTH_ERRORS.TERMS_NOT_ACCEPTED)
      return
    }

    setSignUpData(data)

    if (token) {
      setError('NONE')
      return goNextStep() // Skip step 2 if token is present
    }

    try {
      await dispatch(AuthThunks.checkAccountByEmail(data?.email))
      setError('NONE')
      goNextStep()
    } catch (error) {
      console.error('error', error.data?.code)
      if (error.status === 409) {
        setError(AUTH_ERRORS.CHECK_EMAIL_FAILED)
      }
    }
  }

  const submitStepTwo = async (stepTwoData = {}) => {
    try {
      const response = await dispatch(
        AuthThunks.validateEmailVerificationCode({
          email: signUpData?.email,
          code: stepTwoData?.verificationCode
        })
      )
      setEmailToken(response.data?.email_token)
      goNextStep()
      setVerificationCodeInvalid(false)
    } catch (error) {
      console.error('error', error)
      if (error.status === 409) {
        setVerificationCodeInvalid(true)
      }
    }
  }

  const submitStepThree = async (data) => {
    delete signUpData.token

    try {
      await dispatch(
        AuthThunks.confirmSignup({
          ...signUpData,
          ...data,
          email_token: emailToken,
          language: 'en',
          inviter_email: token ? decodedToken?.data?.inviter_email : null,
          inviter_id: token ? decodedToken?.data?.inviter_id : null,
          inviter_relationship: token ? decodedToken?.data?.relationship : null,
          sign_up_page_url: window.location.href
        })
      )

      if (decodedToken?.data?.relationship !== RELATIONSHIP_TYPES.COWORKER) {
        return setSubmissionSuccess(true)
      }

      if (decodedToken?.data?.relationship === RELATIONSHIP_TYPES.COWORKER) {
        return goToLoginPage(true)
      }
    } catch (error) {
      console.error('error', error)
      if (error.status === 403) {
        setError(AUTH_ERRORS.INVALID_EMAIL_TOKEN)
      }

      if (error.status === 409) {
        setError(AUTH_ERRORS.SIGNUP_FAILED)
      }
    }
  }

  const onPreviousStep = () => {
    if (currentStepIndex > 0) setCurrentStepIndex((prevIndex) => prevIndex - 1)
  }

  const steps = token
    ? [
        { component: StepOne, onSubmit: submitStepOne },
        { component: StepThree, onSubmit: submitStepThree }
      ]
    : [
        { component: StepOne, onSubmit: submitStepOne },
        { component: StepTwo, onSubmit: submitStepTwo },
        { component: StepThree, onSubmit: submitStepThree }
      ]

  const currentStep = steps[currentStepIndex]
  const shouldShowStepCounter =
    !isStepCounterBlocked(error) && !isSubmissionSuccess

  const errorBanners = [
    {
      errors: [AUTH_ERRORS.INVALID_CREDENTIALS],
      bannerContent: {
        title: t('error.incorrect-credentials'),
        description: t('error.incorrect-credentials-message')
      }
    },
    {
      errors: [AUTH_ERRORS.TERMS_NOT_ACCEPTED],
      bannerContent: {
        title: t('signup.the-fields-are-incomplete'),
        description: t('signup.please-accept-terms')
      }
    },
    {
      errors: [AUTH_ERRORS.INVALID_EMAIL_TOKEN],
      bannerContent: {
        title: t('signup.invitation-link-has-expired'),
        description: t('signup.invitation-link-has-expired-body')
      }
    },
    {
      errors: [AUTH_ERRORS.SIGNUP_FAILED],
      bannerContent: {
        title: t('error.something-went-wrong'),
        description: t('error.something-went-wrong-message')
      }
    }
  ]

  const renderCurrentStep = () => {
    if (error === AUTH_ERRORS.CHECK_EMAIL_FAILED) {
      return (
        <SignUpResult
          title={t('signup.you-already-have-an-account')}
          message={t('signup.you-already-have-an-account-body')}
          onButtonClick={() => setError('NONE')}
          buttonText={t('login.return-to-sign-in-page')}
        />
      )
    }

    if (error === AUTH_ERRORS.INVALID_EMAIL_TOKEN) {
      return (
        <SignUpResult
          onButtonClick={() => forceGoToPage('/signup')}
          buttonText={t('signup.proceed-to-signup')}
        />
      )
    }

    if (isSubmissionSuccess) {
      return (
        <SignUpResult
          dataCy="signup-success"
          title={t('signup.we-got-your-request')}
          message={t('signup.we-got-your-request-body')}
          buttonText={t('login.return-to-sign-in-page')}
          onButtonClick={() => navigate('/')}
        />
      )
    }

    const StepComponent = currentStep.component
    return (
      <StepComponent
        onSubmit={currentStep.onSubmit}
        onBack={onPreviousStep}
        signUpData={signUpData}
        decodedToken={decodedToken}
        token={token}
      />
    )
  }

  useEffect(() => {
    if (token) {
      setEmailToken(token)
    }
  }, [token])

  const bannerToShow = errorBanners.find(({ errors }) => errors.includes(error))

  const content = (
    <AuthLayout>
      <LoginCard>
        <SignupResponsiveWrapper>
          {isVerificationCodeInvalid && (
            <Banner
              variant="error"
              mode="dark"
              style={{ marginBottom: 32, maxWidth: '100%' }}
              bannerContent={{
                title: t('signup.verification-code-expired'),
                description: t('signup.please-request-a-new-verification-code')
              }}
            />
          )}

          {bannerToShow && (
            <Banner
              mode="dark"
              style={{ marginBottom: 32, maxWidth: '100%' }}
              variant="error"
              bannerContent={bannerToShow.bannerContent}
            />
          )}

          {shouldShowStepCounter && (
            <SignUpStepsCounter>
              {t('general.step')} {currentStepIndex + 1}{' '}
              <span>
                {t('general.of')} {steps.length}
              </span>
            </SignUpStepsCounter>
          )}

          {renderCurrentStep()}

          <FooterText style={{ marginTop: 0 }}>
            {t('signup.already-have-an-account')}
            <SignUpLink onClick={() => navigate('/login')}>
              {t('login.sign-in')}
            </SignUpLink>
          </FooterText>
        </SignupResponsiveWrapper>
      </LoginCard>
    </AuthLayout>
  )

  return responsiveBreakpointsRollout ? (
    <AuthRolloutBoundary page={responsiveBreakpointsPage}>
      {content}
    </AuthRolloutBoundary>
  ) : (
    content
  )
}

Signup.propTypes = {
  responsiveBreakpointsPage: PropTypes.string,
  responsiveBreakpointsRollout: PropTypes.bool
}

export default Signup
